import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Dialog,
  IconButton,
} from "@mui/material";
import { Lock } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useUrlParams } from "hooks/useUrlParams";
import { useTheme } from "@emotion/react";
import StyledButton from "default/components/StyledButton";
import Loading from "default/components/Loading";
import { getSystemUserEmail as getUserEmail, updateSystemUserEmail as updateUserEmail } from "utils/api";
import PotionLayout from "../components/PotionLayout";

// Potion styled dialog
const PotionDialog = ({ open, onClose, title, children, theme }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        backgroundColor: "transparent",
        boxShadow: "none",
        width: "88%",
      },
    }}
  >
    <Box
      sx={{
        backgroundColor: "rgba(19, 67, 58, 0.95)",
        border: "1px solid rgba(255, 255, 255, 0.6)",
        boxShadow:
          "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
        borderRadius: "8px",
        padding: "32px 24px",
        position: "relative",
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: "8px",
          top: "8px",
          color: "#FFFFFF",
        }}
      >
        <CloseIcon />
      </IconButton>
      <Typography
        variant="h6"
        sx={{
          fontFamily: theme.system.fonts.heading,
          fontSize: "16px",
          fontWeight: "bold",
          color: "#FFFFFF",
          mb: 3,
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  </Dialog>
);

function EmailSettings() {
  const { work } = useUrlParams();
  const { t } = useTranslation(["common", work]);
  const theme = useTheme();

  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [doNotReceiveNotifications, setDoNotReceiveNotifications] =
    useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const fetchCurrentEmail = async () => {
      try {
        const emailData = await getUserEmail();
        setCurrentEmail(emailData.email_address);
        setDoNotReceiveNotifications(!emailData.mail_delivery_enabled);
      } catch (error) {
        console.error("Failed to fetch current email:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentEmail();
  }, []);

  const handleUpdateEmail = async () => {
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError(t("common:EmailSettings.invalid_email_format"));
      return;
    }

    setEmailError("");
    setIsProcessing(true);
    try {
      const emailToSend = newEmail || null;
      await updateUserEmail(emailToSend, doNotReceiveNotifications);

      const updatedEmailData = await getUserEmail();
      setCurrentEmail(updatedEmailData.email_address);
      setDoNotReceiveNotifications(!updatedEmailData.mail_delivery_enabled);
      setNewEmail("");

      setDialogMessage(t("common:EmailSettings.update_success"));
      setOpenDialog(true);
    } catch (error) {
      console.error("Failed to update email:", error);
      setDialogMessage(t("common:EmailSettings.update_fail"));
      setOpenDialog(true);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <PotionLayout>
        <Loading />
      </PotionLayout>
    );
  }

  return (
    <PotionLayout>
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            marginBottom: 4,
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.components.sectionHeader,
            })}
          >
            E-m<span style={{ color: "#8CB808" }}>a</span>il
          </Typography>
        </Box>

        {/* 現在のメールアドレス */}
        <Box sx={{ width: "100%", maxWidth: 400, marginBottom: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={currentEmail}
            disabled
            label={t("common:EmailSettings.current_email")}
            placeholder={t("common:EmailSettings.current_email")}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Lock sx={{ color: theme.system.colors.text.primary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: `${theme.system.colors.text.primary}`,
                color: `${theme.system.colors.text.primary}`,
                fontFamily: theme.system.fonts.body,
              },
              "& .MuiOutlinedInput-root.Mui-disabled": {
                backgroundColor: `${theme.system.colors.text.primary}20`,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.system.colors.text.primary,
                },
              },
              "& .MuiInputLabel-root.Mui-disabled": {
                color: theme.system.colors.text.primary,
                fontFamily: theme.system.fonts.body,
              },
            }}
          />
        </Box>

        {/* 新しいメールアドレス */}
        <Box sx={{ width: "100%", maxWidth: 400, marginBottom: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            placeholder={t("common:EmailSettings.new_email")}
            error={!!emailError}
            helperText={emailError}
            sx={{
              "& .MuiInputBase-input": {
                color: theme.system.colors.text.primary,
                fontFamily: theme.system.fonts.body,
              },
              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: emailError
                    ? "error.main"
                    : theme.system.colors.text.primary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: emailError
                    ? "error.main"
                    : theme.system.colors.text.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: emailError
                    ? "error.main"
                    : theme.system.colors.text.primary,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: theme.system.colors.text.primary,
                opacity: 0.7,
                fontFamily: theme.system.fonts.body,
              },
              "& .MuiFormHelperText-root": {
                color: "error.main",
                fontFamily: theme.system.fonts.body,
              },
            }}
          />
        </Box>

        {/* お知らせメールを受け取らない */}
        <Box sx={{ width: "100%", maxWidth: 400, marginBottom: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={doNotReceiveNotifications}
                onChange={(e) => setDoNotReceiveNotifications(e.target.checked)}
                sx={{
                  color: theme.system.colors.text.secondary,
                  "&.Mui-checked": {
                    color: theme.system.colors.text.secondary,
                  },
                }}
              />
            }
            label={t("common:EmailSettings.do_not_receive_notifications")}
            sx={{
              color: theme.system.colors.text.secondary,
              fontFamily: theme.system.fonts.body,
            }}
          />
        </Box>

        {/* 変更ボタン */}
        <StyledButton
          variant="contained"
          onClick={handleUpdateEmail}
          disabled={isProcessing}
          sx={{
            marginTop: 4,
            background: isProcessing ? "white" : "transparent",
          }}
        >
          {t("common:button.update")}
        </StyledButton>
      </Box>

      <PotionDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={dialogMessage}
        theme={theme}
      >
        <StyledButton
          variant="contained"
          onClick={() => setOpenDialog(false)}
          sx={{
            width: "100%",
            border: "1.5px solid rgba(255,255,255,0.6)",
            color: "#FFFFFF",
            fontSize: "14px",
          }}
        >
          {t("common:button.close")}
        </StyledButton>
      </PotionDialog>
    </PotionLayout>
  );
}

export default EmailSettings;
